/**
 * MobileTeamsTab
 *
 * Mobile card-based replacement for the PrimeReact DataTable in TeamsTab.
 * Shows each team as a touch-friendly card with name, client, localization,
 * country, managers/SDM summary, and vfact preview.
 * Supports search, pagination, tap-to-open detail modal, and action buttons.
 */
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
    Box,
    Card,
    CardContent,
    CardActionArea,
    TextField,
    InputAdornment,
    Typography,
    Pagination,
    Fade,
    IconButton,
    Chip,
    Collapse,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import moment from "moment";
import Langs, { Localize } from "../../../app/lang/langs";
import { getTeams, setSelectedTeams } from "../../../components/teams/teamsSlice";
import { getManagersProfiles } from "../../../components/permissions/permissionsSlice";
import { isAdmin } from "../../../app/utils/helpers";
import ExcelManager from "../../../app/managers/excelManager";
import CollabModalDetails from "../../collabs/CollabModalDetails";
import MobileManagersListModal from "./MobileManagersListModal";
import MobileCollabsListModal from "./MobileCollabsListModal";
import MobileTeamsDetailsModal from "./MobileTeamsDetailsModal";
import MobileAddTeamModal from "./MobileAddTeamModal";
import MobileInactiveTeamsModal from "./MobileInactiveTeamsModal";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const TRANSITION_SPRING = "all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)";
const PAGE_SIZE = 20;

export default function MobileTeamsTab() {
    const mounted = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const urlParams = useParams();

    const teamsList = useSelector((state) => state.teams.allTeams);
    const userRoleId = useSelector((state) => state.login.userRoleId);
    const managersProfiles = useSelector((state) => state.permissions.managersProfiles);

    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(1);
    const [expandedCardId, setExpandedCardId] = useState(null);

    // Modals
    const [managersListModalVisibility, setManagersModalVisibility] = useState(false);
    const [collabsListModalVisibility, setCollabModalListVisibility] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [displayTeamsDetailsModal, setDisplayTeamsDetailsModal] = useState(false);
    const [onPreviewTeam, setOnPreviewTeam] = useState(null);
    const [addTeamModalVisibility, setAddTeamModalVisbility] = useState(false);
    const [inactiveModalVisibility, setInactiveModalVisibility] = useState(false);

    useEffect(() => {
        if (!mounted.current) {
            dispatch(getTeams());
            dispatch(getManagersProfiles({}));
        }
        mounted.current = true;
    }, [mounted]);

    useEffect(() => {
        if (urlParams.view === "clients" && urlParams.subView === "1" && urlParams.label) {
            setSearchText(urlParams.label);
        }
    }, [urlParams]);

    /* ---- Filtering ---- */
    const filteredData = useMemo(() => {
        let list = teamsList || [];
        if (searchText.trim()) {
            const terms = searchText.toLowerCase().split(" ").filter(Boolean);
            list = list.filter((team) => {
                const searchStr = [
                    team.label,
                    team.client,
                    team.localization,
                    team.country_name,
                    team.sous_service,
                    team.vfact,
                    ...(team.managers || []).map((m) => `${m.prenom} ${m.nom}`),
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return terms.every((term) => searchStr.includes(term));
            });
        }
        return list;
    }, [teamsList, searchText]);

    useEffect(() => {
        setPage(1);
    }, [searchText]);

    const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
    const paginatedData = filteredData.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    /* ---- Export ---- */
    const exportTeamsList = () => {
        const dataToExport = filteredData.reduce((array, current) => {
            let row = {
                "ID": current.id || "",
                "Nom": current.label || "",
                "Client": current.client || "",
                "Localization": current.localization || "",
                "Pays": current.country_name || "",
            };
            managersProfiles.forEach(profile => {
                row[profile.name] = current.managers
                    ? current.managers.filter((m) => m.type === profile.name).map((m) => m.prenom + " " + m.nom).join(", ")
                    : "";
            });
            row["Sous service"] = current.sous_service || "";
            row["Codes des prestations V-Fact"] = current.vfact
                ? current.vfact.split("\n").join(" | ")
                : "";
            array.push(row);
            return array;
        }, []);
        ExcelManager.getInstance().generateAndDownloadExcelFromData(dataToExport, "teams");
    };

    /* ---- Handlers ---- */
    const onCardTap = (team) => {
        setDisplayTeamsDetailsModal(true);
        setOnPreviewTeam(team);
    };

    const jumpToPlanning = (e, team) => {
        e.stopPropagation();
        dispatch(setSelectedTeams([team.value]));
        navigate(`/planning/${team.value}/MONTHLY${moment().format("DDMMYYYY")}`);
    };

    const toggleExpand = (e, teamId) => {
        e.stopPropagation();
        setExpandedCardId((prev) => (prev === teamId ? null : teamId));
    };

    /* ---- Helpers ---- */
    const getManagersByProfile = (team, profileName) =>
        (team.managers || []).filter((m) => m.type === profileName);
    const getVfacts = (team) =>
        team.vfact ? team.vfact.split("\n").filter(Boolean) : [];

    return (
        <Box
            sx={{
                px: 2,
                pt: 1.5,
                pb: 2.5,
                mt: 0,
                height: "calc(100vh - 130px)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Top action row */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 1,
                }}
            >
                {/* Search bar */}
                <TextField
                    fullWidth
                    size="small"
                    placeholder={Localize("Search") + "..."}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: "rgba(255,144,21,0.45)" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        "& input": { fontSize: "16px" },
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "16px",
                            bgcolor: "rgba(255,255,255,0.8)",
                            backdropFilter: "blur(16px) saturate(1.3)",
                            WebkitBackdropFilter: "blur(16px) saturate(1.3)",
                            transition: TRANSITION_SMOOTH,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.8)",
                            "& fieldset": {
                                borderColor: "rgba(255,144,21,0.08)",
                                transition: TRANSITION_SMOOTH,
                            },
                            "&:hover fieldset": {
                                borderColor: "rgba(255,144,21,0.2)",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: "rgba(255,144,21,0.5)",
                                boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
                            },
                        },
                    }}
                />

                {/* Action buttons */}
                {isAdmin(userRoleId) && (
                    <IconButton
                        onClick={() => setInactiveModalVisibility(true)}
                        sx={{
                            width: 42,
                            height: 42,
                            bgcolor: "rgba(255,255,255,0.7)",
                            backdropFilter: "blur(12px)",
                            borderRadius: "14px",
                            border: "1px solid rgba(0,0,0,0.04)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                            transition: TRANSITION_SMOOTH,
                            "&:hover": { bgcolor: "rgba(255,255,255,0.9)", boxShadow: "0 4px 12px rgba(0,0,0,0.06)" },
                            "&:active": { transform: "scale(0.93)" },
                        }}
                    >
                        <ArchiveOutlinedIcon sx={{ fontSize: "1.2rem", color: "rgba(0,0,0,0.5)" }} />
                    </IconButton>
                )}
                <IconButton
                    onClick={exportTeamsList}
                    sx={{
                        width: 42,
                        height: 42,
                        bgcolor: "rgba(255,255,255,0.7)",
                        backdropFilter: "blur(12px)",
                        borderRadius: "14px",
                        border: "1px solid rgba(0,0,0,0.04)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        transition: TRANSITION_SMOOTH,
                        "&:hover": { bgcolor: "rgba(255,255,255,0.9)", boxShadow: "0 4px 12px rgba(0,0,0,0.06)" },
                        "&:active": { transform: "scale(0.93)" },
                    }}
                >
                    <FileDownloadOutlinedIcon sx={{ fontSize: "1.2rem", color: "rgba(0,0,0,0.5)" }} />
                </IconButton>
                <IconButton
                    onClick={() => setAddTeamModalVisbility(true)}
                    sx={{
                        width: 42,
                        height: 42,
                        background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                        color: "#fff",
                        borderRadius: "14px",
                        boxShadow: "0 4px 14px rgba(255,144,21,0.3), 0 1px 4px rgba(255,144,21,0.15)",
                        transition: TRANSITION_SMOOTH,
                        "&:hover": { boxShadow: "0 6px 20px rgba(255,144,21,0.4)" },
                        "&:active": { transform: "scale(0.93)" },
                    }}
                >
                    <AddIcon sx={{ fontSize: "1.3rem" }} />
                </IconButton>
            </Box>

            {/* Results count */}
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    mb: 1,
                    px: 0.5,
                    fontWeight: 600,
                    letterSpacing: "0.03em",
                    opacity: 0.55,
                    fontSize: "0.72rem",
                    textTransform: "uppercase",
                }}
            >
                {filteredData.length} {Localize("results")}
            </Typography>

            {/* Card list */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch",
                    pb: 2,
                    scrollBehavior: "smooth",
                    "&::-webkit-scrollbar": { width: 5 },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                        background: "rgba(255,144,21,0.15)",
                        borderRadius: 3,
                        "&:hover": { background: "rgba(255,144,21,0.25)" },
                    },
                }}
            >
                {paginatedData.map((team, index) => {
                    const vfacts = getVfacts(team);
                    const isExpanded = expandedCardId === team.value;

                    return (
                        <Fade in key={team.value || index} timeout={250 + index * 50}>
                            <Card
                                elevation={0}
                                sx={{
                                    mb: 1.5,
                                    borderRadius: "18px",
                                    overflow: "hidden",
                                    bgcolor: "rgba(255,255,255,0.88)",
                                    backdropFilter: "blur(20px) saturate(1.3)",
                                    WebkitBackdropFilter: "blur(20px) saturate(1.3)",
                                    border: "1px solid rgba(255,144,21,0.06)",
                                    boxShadow:
                                        "0 4px 16px rgba(0,0,0,0.04), 0 1px 4px rgba(0,0,0,0.03), 0 0 0 0.5px rgba(255,255,255,0.5) inset",
                                    transition: TRANSITION_SMOOTH,
                                    "&:active": {
                                        transform: "scale(0.975)",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                                    },
                                }}
                            >
                                <CardActionArea
                                    onClick={() => onCardTap(team)}
                                    sx={{ p: 0 }}
                                >
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 1.5 } }}>
                                        {/* Top row: team name + planning icon */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                mb: 0.75,
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                                                {/* Avatar */}
                                                <Box
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: "14px",
                                                        background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                                                        color: "#fff",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontWeight: 800,
                                                        fontSize: "0.88rem",
                                                        flexShrink: 0,
                                                        boxShadow: "0 4px 12px rgba(255,144,21,0.25), 0 1px 3px rgba(255,144,21,0.15)",
                                                        letterSpacing: "-0.02em",
                                                    }}
                                                >
                                                    {(team.label?.[0] || "E").toUpperCase()}
                                                </Box>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: "0.92rem",
                                                            lineHeight: 1.3,
                                                            letterSpacing: "-0.015em",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                            color: "rgba(0,0,0,0.85)",
                                                        }}
                                                    >
                                                        {team.label}
                                                    </Typography>
                                                    {team.client && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize: "0.73rem",
                                                                opacity: 0.6,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 0.5,
                                                                color: "rgba(0,0,0,0.5)",
                                                            }}
                                                        >
                                                            <BusinessOutlinedIcon sx={{ fontSize: "0.82rem", opacity: 0.6 }} />
                                                            {team.client}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>

                                            {/* Planning shortcut */}
                                            <IconButton
                                                onClick={(e) => jumpToPlanning(e, team)}
                                                size="small"
                                                sx={{
                                                    width: 38,
                                                    height: 38,
                                                    background: "linear-gradient(135deg, rgba(255,144,21,0.1) 0%, rgba(255,193,0,0.06) 100%)",
                                                    color: "rgb(255,144,21)",
                                                    flexShrink: 0,
                                                    ml: 1,
                                                    borderRadius: "12px",
                                                    border: "1px solid rgba(255,144,21,0.1)",
                                                    transition: TRANSITION_SPRING,
                                                    "&:hover": {
                                                        background: "linear-gradient(135deg, rgba(255,144,21,0.18) 0%, rgba(255,193,0,0.12) 100%)",
                                                        boxShadow: "0 4px 12px rgba(255,144,21,0.15)",
                                                    },
                                                    "&:active": { transform: "scale(0.9)" },
                                                }}
                                            >
                                                <CalendarMonthOutlinedIcon sx={{ fontSize: "1.1rem" }} />
                                            </IconButton>
                                        </Box>

                                        {/* Info pills row */}
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                            {team.localization && (
                                                <Chip
                                                    icon={<LocationOnOutlinedIcon sx={{ fontSize: "0.82rem" }} />}
                                                    label={team.localization}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        fontSize: "0.7rem",
                                                        height: 26,
                                                        borderRadius: "10px",
                                                        borderColor: "rgba(0,0,0,0.08)",
                                                        bgcolor: "rgba(255,255,255,0.5)",
                                                        backdropFilter: "blur(8px)",
                                                        fontWeight: 500,
                                                    }}
                                                />
                                            )}
                                            {team.country_name && (
                                                <Chip
                                                    icon={<PublicOutlinedIcon sx={{ fontSize: "0.82rem" }} />}
                                                    label={team.country_name}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        fontSize: "0.7rem",
                                                        height: 26,
                                                        borderRadius: "10px",
                                                        borderColor: "rgba(0,0,0,0.08)",
                                                        bgcolor: "rgba(255,255,255,0.5)",
                                                        backdropFilter: "blur(8px)",
                                                        fontWeight: 500,
                                                    }}
                                                />
                                            )}
                                            {team.sous_service && (
                                                <Chip
                                                    label={team.sous_service}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        fontSize: "0.7rem",
                                                        height: 26,
                                                        borderRadius: "10px",
                                                        borderColor: "rgba(0,0,0,0.08)",
                                                        bgcolor: "rgba(255,255,255,0.5)",
                                                        backdropFilter: "blur(8px)",
                                                        fontWeight: 500,
                                                    }}
                                                />
                                            )}
                                        </Box>

                                        {/* Managers summary by profile */}
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                                            {managersProfiles.map((profile) => {
                                                const profileManagers = getManagersByProfile(team, profile.name);
                                                if (profileManagers.length === 0) return null;
                                                return (
                                                    <Box
                                                        key={profile.id}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 0.5,
                                                            px: 1,
                                                            py: 0.25,
                                                            borderRadius: "8px",
                                                            bgcolor: "rgba(17,121,190,0.06)",
                                                            transition: TRANSITION_SMOOTH,
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedTeam(team);
                                                            setManagersModalVisibility(true);
                                                        }}
                                                    >
                                                        <SupervisorAccountOutlinedIcon sx={{ fontSize: "0.9rem", color: "primary.main" }} />
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize: "0.72rem",
                                                                color: "primary.main",
                                                                fontWeight: 700,
                                                                textDecoration: "underline",
                                                                textDecorationStyle: "dotted",
                                                            }}
                                                        >
                                                            {profileManagers.length} {profile.name}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>

                                        {/* Expandable section (V-Fact + date + members link) */}
                                        {(vfacts.length > 0 || team.created_at) && (
                                            <Box sx={{ mt: 0.75, pt: 0.5, borderTop: "1px solid rgba(0,0,0,0.04)" }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontSize: "0.7rem",
                                                            color: "text.secondary",
                                                            opacity: 0.6,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 0.5,
                                                            transition: TRANSITION_SMOOTH,
                                                        }}
                                                        onClick={(e) => toggleExpand(e, team.value)}
                                                    >
                                                        {isExpanded ? (
                                                            <ExpandLessIcon sx={{ fontSize: "1rem" }} />
                                                        ) : (
                                                            <ExpandMoreIcon sx={{ fontSize: "1rem" }} />
                                                        )}
                                                        {vfacts.length > 0
                                                            ? `${vfacts.length} V-Fact`
                                                            : Localize("Details")}
                                                    </Typography>

                                                    {/* Members link */}
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontSize: "0.72rem",
                                                            color: "success.dark",
                                                            fontWeight: 700,
                                                            textDecoration: "underline",
                                                            textDecorationStyle: "dotted",
                                                            cursor: "pointer",
                                                            transition: TRANSITION_SMOOTH,
                                                            "&:active": { opacity: 0.7 },
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedTeam(team);
                                                            setCollabModalListVisibility(true);
                                                        }}
                                                    >
                                                        <Langs str="MEMBERS" />
                                                    </Typography>
                                                </Box>
                                                <Collapse in={isExpanded}>
                                                    <Box sx={{ pt: 1 }}>
                                                        {/* V-Fact list */}
                                                        {vfacts.length > 0 && (
                                                            <Box sx={{ mb: 1 }}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontWeight: 700,
                                                                        fontSize: "0.68rem",
                                                                        color: "rgba(255,144,21,0.7)",
                                                                        textTransform: "uppercase",
                                                                        letterSpacing: "0.06em",
                                                                    }}
                                                                >
                                                                    V-Fact
                                                                </Typography>
                                                                {vfacts.map((vf, i) => (
                                                                    <Typography
                                                                        key={i}
                                                                        variant="caption"
                                                                        sx={{
                                                                            display: "block",
                                                                            fontSize: "0.72rem",
                                                                            color: "text.secondary",
                                                                            pl: 1.5,
                                                                            position: "relative",
                                                                            "&::before": {
                                                                                content: '"\\2022"',
                                                                                position: "absolute",
                                                                                left: 0,
                                                                                color: "rgba(255,144,21,0.5)",
                                                                            },
                                                                        }}
                                                                    >
                                                                        {vf}
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        )}

                                                        {/* Created date */}
                                                        {team.created_at && (
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                                <AccessTimeOutlinedIcon
                                                                    sx={{
                                                                        fontSize: "0.82rem",
                                                                        color: "text.secondary",
                                                                        opacity: 0.4,
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{ fontSize: "0.7rem", opacity: 0.5, fontWeight: 500 }}
                                                                >
                                                                    {moment(team.created_at).format("DD/MM/YYYY HH:mm")}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </Box>
                                        )}
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Fade>
                    );
                })}

                {paginatedData.length === 0 && (
                    <Fade in timeout={400}>
                        <Box sx={{ textAlign: "center", py: 10 }}>
                            <Typography
                                variant="body2"
                                sx={{ opacity: 0.35, fontWeight: 500, letterSpacing: "-0.01em" }}
                            >
                                <Langs str="No results" />
                            </Typography>
                        </Box>
                    </Fade>
                )}
            </Box>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_e, value) => setPage(value)}
                        size="small"
                        color="warning"
                        sx={{
                            "& .MuiPaginationItem-root": {
                                borderRadius: "10px",
                                transition: TRANSITION_SMOOTH,
                                "&:hover": { transform: "scale(1.08)" },
                                "&:active": { transform: "scale(0.95)" },
                                "&.Mui-selected": {
                                    boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                                },
                            },
                        }}
                    />
                </Box>
            )}

            {/* Modals */}
            <MobileManagersListModal
                visible={managersListModalVisibility}
                setVisible={setManagersModalVisibility}
                idTeam={selectedTeam && selectedTeam.value}
                teamName={selectedTeam && selectedTeam.label}
            />
            <MobileCollabsListModal
                visible={collabsListModalVisibility}
                setVisible={setCollabModalListVisibility}
                idTeam={selectedTeam && selectedTeam.value}
                teamName={selectedTeam && selectedTeam.label}
            />
            <CollabModalDetails
                page="collabs"
                tabs={["contrat", "societe", "mission", "donneesPerso", "profil"]}
            />
            <MobileTeamsDetailsModal
                visible={displayTeamsDetailsModal}
                setVisible={setDisplayTeamsDetailsModal}
                team={onPreviewTeam}
                setTeam={setOnPreviewTeam}
            />
            <MobileAddTeamModal
                visible={addTeamModalVisibility}
                setVisible={setAddTeamModalVisbility}
            />
            <MobileInactiveTeamsModal
                visible={inactiveModalVisibility}
                setVisible={setInactiveModalVisibility}
            />
        </Box>
    );
}
